import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Reveal, MaskedLine, Eyebrow, EASE } from "./Reveal";
import DonationBox from "./DonationBox";
import DonationAppeal from "./DonationAppeal";

const FALLBACK_STILL =
  "https://customer-assets-v7afamib.emergentagent.net/job_danimorr/artifacts/fab4270bf7dad60d_woods%20dm.jpeg";

const Curtain = ({ side }) => (
  <motion.div
    data-testid={`curtain-${side}`}
    className={`curtain absolute inset-y-0 ${side === "left" ? "left-0" : "right-0"} w-1/2 z-20`}
    initial={{ x: "0%" }}
    whileInView={{ x: side === "left" ? "-101%" : "101%" }}
    viewport={{ once: true, margin: "-120px" }}
    transition={{ duration: 2, delay: 0.4, ease: [0.76, 0, 0.24, 1] }}
  />
);

const Stage = () => {
  const [videoFailed, setVideoFailed] = useState(false);
  const frameRef = useRef(null);
  const rumbleRef = useRef(null);

  // Low bass rumble + wind: starts as the curtains part, stops when scrolled away
  useEffect(() => {
    const audio = rumbleRef.current;
    const frame = frameRef.current;
    if (!audio || !frame) return;
    audio.volume = 0.45;
    let startTimer;
    const tryPlay = () => audio.play().catch(() => {});
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          startTimer = setTimeout(tryPlay, 600);
        } else {
          clearTimeout(startTimer);
          audio.pause();
        }
      },
      { threshold: 0.2 }
    );
    obs.observe(frame);
    // Autoplay policy: if the visitor hasn't tapped yet, start on first interaction
    const unlock = () => {
      if (audio.paused) {
        const r = frame.getBoundingClientRect();
        if (r.top < window.innerHeight * 0.8 && r.bottom > 0) tryPlay();
      }
    };
    document.addEventListener("pointerdown", unlock);
    document.addEventListener("keydown", unlock);
    return () => {
      clearTimeout(startTimer);
      obs.disconnect();
      document.removeEventListener("pointerdown", unlock);
      document.removeEventListener("keydown", unlock);
      audio.pause();
    };
  }, []);

  return (
    <section id="stage" data-testid="stage-section" className="relative bg-ink py-28 lg:py-36 border-t border-cream/10">
      <audio ref={rumbleRef} data-testid="stage-rumble-audio" loop preload="auto">
        <source src="/assets/stage-rumble.ogg" type="audio/ogg" />
        <source src="/assets/stage-rumble.mp3" type="audio/mpeg" />
      </audio>
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
        <Reveal>
          <Eyebrow index="04" label="The Stage" />
        </Reveal>
        <div className="mt-10 max-w-2xl mx-auto text-center">
          <h2 className="font-serif font-light text-cream text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            <MaskedLine delay={0.05}>Curtains up —</MaskedLine>
            <MaskedLine delay={0.15}>
              the film <em className="italic text-gold">never ends.</em>
            </MaskedLine>
          </h2>
          <Reveal delay={0.25}>
            <DonationBox />
          </Reveal>
        </div>

        <DonationAppeal />

        <Reveal delay={0.2} className="mt-14">
          <div
            ref={frameRef}
            data-testid="stage-frame"
            className="relative max-w-5xl mx-auto border border-gold/40 shadow-[0_40px_120px_-30px_rgba(0,0,0,0.9)]"
          >
            <div className="curtain-valance relative z-30 h-10 lg:h-12 border-b-2 border-gold/50" />
            <div className="relative aspect-video bg-black overflow-hidden">
              <div className="absolute inset-x-0 top-0 h-2/3 z-10 pointer-events-none stage-spotlight" />
              {!videoFailed ? (
                <video
                  data-testid="stage-video"
                  className="w-full h-full object-cover analog-grade"
                  autoPlay
                  muted
                  loop
                  playsInline
                >
                  <source src="/assets/stage-beast.webm" type="video/webm" />
                  <source
                    src="/assets/stage-beast.mp4"
                    type="video/mp4"
                    onError={() => setVideoFailed(true)}
                  />
                </video>
              ) : (
                <img
                  data-testid="stage-fallback-image"
                  src={FALLBACK_STILL}
                  alt="Forest loop — still frame"
                  className="kenburns w-full h-full object-cover analog-grade"
                />
              )}
              <div className="absolute inset-0 pointer-events-none z-[6] bg-gradient-to-t from-ink/50 via-transparent to-ink/30" />
              <span className="absolute bottom-3 right-4 z-10 font-mono text-[10px] tracking-[0.25em] text-gold">
                ● PLAYING
              </span>
              <Curtain side="left" />
              <Curtain side="right" />
            </div>
            <div className="relative z-30 h-3 bg-gradient-to-b from-[#3a2412] to-[#1a1209] border-t border-gold/30" />
          </div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cream/35 text-center mt-6">
            House lights down — the loop runs all night
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default Stage;
